import { useEffect, useMemo } from 'react';

import {
    BundleState,
    useBundleUpdatedSubscription,
    useGetBundlesFilterQuery,
    useGetSitesQuery,
    useGetToteamUsersQuery,
    useGetWarehouseColumnsQuery,
} from 'data/__generated__';

import { DataTable } from 'components/DataTable';

import { buildBundlesColumns, BundleFilterInput, BundleRow } from '../constants/bundlesColumns';

const PAGE_LIMIT = 30;

const defaultFilters: BundleFilterInput = {
    state: [BundleState.Created, BundleState.Started, BundleState.InProgress, BundleState.Paused],
};

type BundlesListProps = {
    selectedIds: string[];
    onSelectionChange: (selectedIds: string[]) => void;
    onBundlesChange: (bundles: BundleRow[]) => void;
};

export const BundlesList = ({ selectedIds, onSelectionChange, onBundlesChange }: BundlesListProps) => {
    const { data: usersData } = useGetToteamUsersQuery();
    const { data: warehousesData } = useGetWarehouseColumnsQuery();
    const { data: sitesData } = useGetSitesQuery();

    const { loading, data, error, refetch, fetchMore } = useGetBundlesFilterQuery({
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: { filters: defaultFilters, offset: 0, limit: PAGE_LIMIT },
    });

    useBundleUpdatedSubscription();

    if (error) {
        throw new Error('Une erreur est survenue lors de la récupération des bundles');
    }

    const columns = useMemo(
        () =>
            buildBundlesColumns({
                operators: usersData?.toteamUsers ?? [],
                warehouseColumns: warehousesData?.warehouseColumns ?? [],
                sites: sitesData?.sites ?? [],
            }),
        [usersData, warehousesData, sitesData],
    );

    const bundles = useMemo(() => data?.bundlesFilter.bundles ?? [], [data]);
    const hasMore = data?.bundlesFilter.hasMore ?? false;

    // Surface the loaded bundles to the parent so the flow quick-select buttons can act on them.
    useEffect(() => {
        onBundlesChange(bundles);
    }, [bundles, onBundlesChange]);

    return (
        <DataTable<BundleRow, BundleFilterInput>
            columns={columns}
            data={bundles}
            filterMode="server"
            getRowId={(row) => row._id}
            getRowLink={(row) => `/bundle/${row._id}`}
            hasMore={hasMore}
            isLoading={loading}
            refetch={async (vars) => {
                await refetch({ ...vars, offset: 0, limit: PAGE_LIMIT });
            }}
            fetchMore={async ({ variables }) => {
                await fetchMore({ variables: { ...variables, limit: PAGE_LIMIT } });
            }}
            defaultFilters={defaultFilters}
            defaultSortBy="createdAt"
            defaultSortOrder="desc"
            searchPlaceholder="Numéro de bundle ou nom du site"
            searchFilterKey="searchValue"
            enableSelection
            selectedKeys={selectedIds}
            onSelectionChange={onSelectionChange}
        />
    );
};
